// src/pages/Blog.js
import React, { useEffect } from 'react';
import { posts } from '../posts';

const sans = "'DM Sans', system-ui, sans-serif";
const serif = "'DM Serif Display', Georgia, serif";

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'long', year: 'numeric',
  });
}

export default function Blog({ onBack, onPost, onStartTracking }) {
  useEffect(() => {
    document.title = 'Journal — ApptNote';
  }, []);

  return (
    <div style={styles.page}>
      <div style={styles.inner}>

        <button style={styles.back} onClick={onBack}>← Back to ApptNote</button>

        <div style={styles.logo}>
          <span style={styles.logoAppt}>Appt</span>
          <span style={styles.logoNote}>Note</span>
        </div>

        <div style={styles.hero}>
          <p style={styles.label}>Journal</p>
          <h1 style={styles.heroTitle}>Women's health,<br />honestly written.</h1>
          <p style={styles.heroSub}>
            Articles to help you understand what's happening, prepare for your
            appointments, and advocate for yourself.
          </p>
        </div>

        <hr style={styles.divider} />

        <div style={styles.grid}>
          {posts.map(post => (
            <div
              key={post.slug}
              style={styles.card}
              onClick={() => onPost(post.slug)}
              onMouseEnter={e => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(44,44,42,0.08)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div style={styles.cardMeta}>
                <span style={styles.cardDate}>{formatDate(post.date)}</span>
                <span style={styles.cardDot} />
                <span style={styles.cardReadTime}>{post.readTime}</span>
              </div>
              <h2 style={styles.cardTitle}>{post.title}</h2>
              <p style={styles.cardDesc}>{post.description}</p>
              <span style={styles.cardLink}>Read article →</span>
            </div>
          ))}
        </div>

        <div style={styles.cta}>
          <p style={styles.ctaLabel}>ApptNote</p>
          <p style={styles.ctaTitle}>Track your symptoms.<br />Build your record.</p>
          <p style={styles.ctaText}>
            ApptNote turns your daily symptom logs into AI-powered insights and
            a doctor-ready report — so you walk in prepared and get taken seriously.
          </p>
          <button style={styles.ctaButton} onClick={onStartTracking}>
            Start tracking free →
          </button>
        </div>

      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#FAF7F2',
    fontFamily: sans,
  },
  inner: {
    padding: '24px 24px 80px',
    maxWidth: '680px',
    margin: '0 auto',
  },
  back: {
    fontFamily: sans, fontSize: '14px', color: '#6c8396', fontWeight: '400',
    background: 'none', border: 'none', padding: 0, cursor: 'pointer',
    marginBottom: '32px', display: 'block',
  },
  logo: {
    fontFamily: sans, fontSize: '22px', fontWeight: '700', marginBottom: '48px',
  },
  logoAppt: { color: '#2C2C2A' },
  logoNote: { color: '#D4A5A5' },

  hero: { marginBottom: '40px' },
  label: {
    fontFamily: sans, fontSize: '11px', fontWeight: '600',
    letterSpacing: '0.12em', textTransform: 'uppercase',
    color: '#7A9E87', marginBottom: '16px',
  },
  heroTitle: {
    fontFamily: serif, fontSize: '40px', fontWeight: '400',
    color: '#1a1a18', lineHeight: '1.15', marginBottom: '16px',
    letterSpacing: '-0.01em',
  },
  heroSub: {
    fontFamily: sans, fontSize: '16px', color: '#4a6070',
    lineHeight: '1.65', fontWeight: '300',
  },

  divider: {
    border: 'none', borderTop: '1px solid rgba(44,44,42,0.08)', marginBottom: '40px',
  },

  grid: { display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '48px' },
  card: {
    backgroundColor: 'white', borderRadius: '14px', padding: '28px',
    cursor: 'pointer', transition: 'transform 0.15s ease, box-shadow 0.15s ease',
    border: '1px solid rgba(44,44,42,0.07)',
  },
  cardMeta: {
    display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '12px',
  },
  cardDate: { fontFamily: sans, fontSize: '12px', color: '#9aabb8', fontWeight: '300' },
  cardDot: { width: '3px', height: '3px', borderRadius: '50%', backgroundColor: '#C4B8A8' },
  cardReadTime: { fontFamily: sans, fontSize: '12px', color: '#9aabb8', fontWeight: '300' },
  cardTitle: {
    fontFamily: serif, fontSize: '22px', fontWeight: '400',
    color: '#1a1a18', lineHeight: '1.3', marginBottom: '10px',
  },
  cardDesc: {
    fontFamily: sans, fontSize: '15px', color: '#4a6070',
    lineHeight: '1.65', marginBottom: '16px', fontWeight: '300',
  },
  cardLink: {
    fontFamily: sans, fontSize: '13px', fontWeight: '500', color: '#7A9E87',
  },

  cta: {
    backgroundColor: 'white', border: '1px solid rgba(212,165,165,0.2)',
    borderLeft: '3px solid #D4A5A5', borderRadius: '14px',
    padding: '32px 28px', marginBottom: '40px',
  },
  ctaLabel: {
    fontFamily: sans, fontSize: '11px', fontWeight: '600',
    letterSpacing: '0.12em', textTransform: 'uppercase',
    color: '#D4A5A5', marginBottom: '12px',
  },
  ctaTitle: {
    fontFamily: serif, fontSize: '26px', fontWeight: '400',
    color: '#1a1a18', marginBottom: '12px', lineHeight: '1.25',
  },
  ctaText: {
    fontFamily: sans, fontSize: '15px', color: '#4a6070',
    lineHeight: '1.65', marginBottom: '24px', fontWeight: '300',
  },
  ctaButton: {
    display: 'inline-block', backgroundColor: '#2C2C2A', color: 'white',
    padding: '13px 28px', borderRadius: '10px', fontWeight: '600',
    fontSize: '15px', border: 'none', cursor: 'pointer', fontFamily: sans,
  },
};
